/**
 * Diplomatie — NAPs, Verträge, Spionage, Rivalen-Intel, Posteingang
 */
import { fixture } from '../data/fixture';
import { t } from '../i18n';
import { openCorpDossier } from './corp-dossier';

export function renderDiplomacy(): HTMLElement {
  const diplo   = fixture.player.diplomacy;
  const naps    = diplo?.naps    ?? [];
  const rivals  = diplo?.rivals  ?? [];
  const intel   = diplo?.intel   ?? [];
  const spies   = diplo?.spies   ?? [];
  const inbox   = diplo?.inbox   ?? [];
  const archive = diplo?.archive ?? [];

  const root = document.createElement('div');
  root.className = 'screen';

  // Buttons/tabs/tables/badges/empties now use the shared FUI component layer
  // (index.html). Only the inbox-card layout remains screen-local.
  const style = document.createElement('style');
  style.textContent = `
    .inbox-card { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px; padding:12px; margin-bottom:10px; }
    .inbox-card.inbox-new { border-color:var(--cyan-dim); }
    .inbox-type-badge { margin-right:6px; }
  `;
  root.appendChild(style);

  const napRows = naps.map(n => `<tr>
    <td style="color:var(--text-hi); font-weight:600"><span class="corp-link" data-corp="${n.corp}" style="cursor:pointer">${n.corp}</span></td>
    <td><span class="chip ${(n.status ?? 'active') === 'active' ? 'ok' : 'warn'}">${(n.status ?? 'active') === 'active' ? t('diplomacy.nap_active') : t('diplomacy.nap_expiring')}</span></td>
    <td style="font-family:var(--mono); font-size:11px; color:var(--text-mid)">${t('diplomacy.nap_since', { cycle: String(n.since) })}</td>
    <td style="font-family:var(--mono); font-size:11px; color:${n.expires ? 'var(--amber)' : 'var(--text-lo)'}">
      ${n.expires ? t('diplomacy.nap_expires', { days: String(n.expires) }) : '—'}
    </td>
    <td><span style="font-size:11px; color:var(--text-lo)">${n.secret ? t('diplomacy.nap_secret') : t('diplomacy.nap_public')}</span></td>
    <td>
      <button class="btn sm red">${t('diplomacy.btn_cancel_nap')}</button>
    </td>
  </tr>`).join('');

  const rivalRows = rivals.map(r => `<tr>
    <td style="color:var(--text-hi); font-weight:600"><span class="corp-link" data-corp="${r.corp}" style="cursor:pointer">${r.corp}</span></td>
    <td style="font-family:var(--mono); font-size:12px; color:var(--amber)">$${(r.nw/1000).toFixed(0)}k</td>
    <td style="font-family:var(--mono); font-size:12px; color:var(--text-mid)">${r.territory}</td>
    <td style="font-family:var(--mono); font-size:12px; color:${r.heat > 50 ? 'var(--red)' : r.heat > 30 ? 'var(--amber)' : 'var(--green)'}">${r.heat}</td>
    <td>${r.dishonored > 0 ? `<span class="chip magenta">DISHONORED ×${r.dishonored}</span>` : '<span style="color:var(--text-lo); font-size:11px">—</span>'}</td>
    <td>
      <button class="btn sm">${t('diplomacy.btn_offer_nap')}</button>
    </td>
  </tr>`).join('');

  const intelRows = intel.map(i => `<tr>
    <td style="font-family:var(--mono); font-size:11px; color:var(--text-lo)">${t('diplomacy.intel_cycle', { cycle: String(i.cycle) })}</td>
    <td style="font-family:var(--mono); font-size:11px; color:var(--cyan-dim)"><span class="corp-link" data-corp="${i.source}" style="cursor:pointer">${i.source}</span></td>
    <td style="color:var(--text-mid)"><span class="corp-link" data-corp="${i.target}" style="cursor:pointer">${i.target}</span></td>
    <td style="color:var(--text-hi); font-size:13px">${i.info}</td>
    <td><span style="font-size:11px; color:${i.confirmed ? 'var(--green)' : 'var(--amber)'}">${i.confirmed ? t('diplomacy.confirmed') : t('diplomacy.unconfirmed')}</span></td>
  </tr>`).join('');

  const inboxBadge = inbox.filter(m => m.needsDecision).length;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div class="screen-title">${t('diplomacy.title')}</div>

    <div class="tabs boxed">
      <button class="tab active" id="dtab-nap">${t('diplomacy.tab_nap')}</button>
      <button class="tab" id="dtab-spy">${t('diplomacy.tab_spy')}</button>
      <button class="tab" id="dtab-intel">${t('diplomacy.tab_intel')}</button>
      <button class="tab" id="dtab-inbox">${t('diplomacy.tab_inbox')}${inboxBadge > 0 ? ` <span style="font-size:11px; background:var(--cyan); color:var(--bg); border-radius:99px; padding:1px 5px">${inboxBadge}</span>` : ''}</button>
    </div>

    <!-- NAPs & Verträge -->
    <div id="dview-nap">
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px">
        <div class="panel">
          <div class="panel-title">${t('diplomacy.panel_naps', { count: String(naps.length) })}</div>
          <table class="dtable">
            <thead><tr><th>${t('diplomacy.col_corp')}</th><th>${t('diplomacy.col_status')}</th><th>${t('diplomacy.col_since')}</th><th>${t('diplomacy.col_duration')}</th><th>${t('diplomacy.col_visibility')}</th><th><span class="sr-only">Aktionen</span></th></tr></thead>
            <tbody>${napRows}</tbody>
          </table>
          ${naps.length === 0 ? `<div class="empty">${t('diplomacy.no_naps')}</div>` : ''}
          <button class="dash-link" style="margin-top:8px">${t('diplomacy.btn_new_nap')}</button>
        </div>

        <div class="panel">
          <div class="panel-title">${t('diplomacy.panel_rivals', { count: String(rivals.length) })}</div>
          <table class="dtable">
            <thead><tr><th>${t('diplomacy.col_corp')}</th><th>${t('diplomacy.col_networth')}</th><th>${t('diplomacy.col_territory')}</th><th>${t('diplomacy.col_heat')}</th><th>${t('diplomacy.col_dishonored')}</th><th><span class="sr-only">Aktionen</span></th></tr></thead>
            <tbody>${rivalRows}</tbody>
          </table>
        </div>
      </div>

      <div class="panel">
        <div class="panel-title">${t('diplomacy.panel_diplo_status')}</div>
        <div style="display:flex; gap:24px; font-size:13px">
          <div>
            <span style="color:var(--text-lo); font-size:11px; text-transform:uppercase; letter-spacing:1px">${t('diplomacy.dishonored_label')}</span>
            <div style="color:${(diplo?.dishonoredUntil ?? 0) > 0 ? 'var(--red)' : 'var(--green)'}; font-family:var(--mono); font-size:14px; margin-top:2px">${(diplo?.dishonoredUntil ?? 0) > 0 ? `DISHONORED (${diplo!.dishonoredUntil} Zyklen)` : t('diplomacy.dishonored_no')}</div>
          </div>
          <div>
            <span style="color:var(--text-lo); font-size:11px; text-transform:uppercase; letter-spacing:1px">${t('diplomacy.betrayal_counter')}</span>
            <div style="font-family:var(--mono); font-size:14px; color:${(diplo?.betrayalCount ?? 0) > 0 ? 'var(--red)' : 'var(--text-hi)'}; margin-top:2px">${diplo?.betrayalCount ?? 0}</div>
          </div>
          <div style="flex:1; font-size:12px; color:var(--text-lo); padding-top:16px">
            ${t('diplomacy.dishonored_effect')}
          </div>
        </div>
      </div>
    </div>

    <!-- Spionage -->
    <div id="dview-spy" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('diplomacy.panel_spies', { count: String(spies.length) })}</div>
        ${spies.length === 0
          ? `<div class="empty">${t('diplomacy.no_spies')}</div>`
          : spies.map(s => `
        <div style="padding:10px 0; border-bottom:1px solid var(--border)">
          <div style="display:flex; justify-content:space-between; align-items:flex-start">
            <div>
              <span style="color:var(--text-hi); font-weight:600">${t('diplomacy.spy_runner', { name: s.runner })}</span>
              <span style="font-family:var(--mono); font-size:11px; color:var(--cyan-dim); margin-left:8px">${s.action}</span>
            </div>
            <span style="font-family:var(--mono); font-size:11px; color:${s.risk > 20 ? 'var(--amber)' : 'var(--text-lo)'}">${t('diplomacy.spy_detection', { risk: s.risk.toFixed(1) })}</span>
          </div>
          <div style="font-size:12px; color:var(--text-lo); margin-top:4px">${t('diplomacy.spy_location', { node: s.location, day: String(s.since) })}</div>
          <div style="font-size:12px; color:var(--text-mid); margin-top:2px">${t('diplomacy.spy_seen', { count: String(s.seen) })}</div>
        </div>`).join('')}
        <button class="dash-link" style="margin-top:8px">${t('diplomacy.btn_new_spy')}</button>
      </div>
    </div>

    <!-- Rivalen-Intel -->
    <div id="dview-intel" style="display:none">
      <div class="panel">
        <div class="panel-title">${t('diplomacy.panel_intel', { count: String(intel.length) })}</div>
        <table class="dtable">
          <thead><tr><th>${t('diplomacy.col_cycle')}</th><th>${t('diplomacy.col_source')}</th><th>${t('diplomacy.col_target')}</th><th>${t('diplomacy.col_info')}</th><th>${t('diplomacy.col_reliability')}</th></tr></thead>
          <tbody>${intelRows}</tbody>
        </table>
      </div>
    </div>

    <!-- Posteingang -->
    <div id="dview-inbox" style="display:none">
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:16px">
        <div class="panel">
          <div class="panel-title">${t('diplomacy.panel_inbox', { count: String(inbox.length) })}</div>
          ${inbox.length === 0
            ? `<div class="empty">${t('diplomacy.no_inbox')}</div>`
            : inbox.map(m => `
          <div class="inbox-card${m.needsDecision ? ' inbox-new' : ''}">
            <div style="display:flex; align-items:center; margin-bottom:6px">
              ${m.needsDecision ? '<span style="width:8px;height:8px;border-radius:50%;background:var(--cyan);display:inline-block;margin-right:6px"></span>' : '<span style="width:8px;height:8px;margin-right:6px;display:inline-block"></span>'}
              <span class="chip ${m.type === 'NAP_OFFER' ? 'info' : 'magenta'} inbox-type-badge">${m.type.replace('_', '-')}</span>
              <span style="font-size:11px; color:var(--text-lo); margin-left:auto">${t('diplomacy.msg_cycle', { cycle: String(m.cycle), hour: m.hour })}</span>
            </div>
            <div style="font-size:12px; color:var(--text-lo); margin-bottom:4px">${t('diplomacy.msg_from')}<span class="corp-link" data-corp="${m.from}" style="color:var(--text-mid);cursor:pointer">${m.from}</span></div>
            <div style="font-size:13px; color:var(--text-hi); margin-bottom:8px">„${m.text}"</div>
            ${m.needsDecision ? `
            <div style="display:flex; gap:6px">
              <button class="inbox-accept-btn btn sm green">${t('diplomacy.btn_accept')}</button>
              <button class="inbox-reject-btn btn sm red">${t('diplomacy.btn_reject')}</button>
              <button class="btn sm">${t('diplomacy.btn_later')}</button>
            </div>` : `
            <button class="btn sm">${t('diplomacy.btn_mark_read')}</button>`}
          </div>`).join('')}
        </div>

        <div class="panel">
          <div class="panel-title">${t('diplomacy.panel_archive', { count: String(archive.length) })}</div>
          ${archive.map(a => `
          <div style="padding:8px 0; border-bottom:1px solid var(--border); font-size:12px">
            <div style="color:var(--text-mid)">${a.type.replace('_', '-')} ${t('diplomacy.archive_to')}<span style="color:var(--text-hi)">${a.to}</span>
              <span style="float:right; color:var(--text-lo); font-family:var(--mono)">${t('diplomacy.archive_cycle', { cycle: String(a.cycle) })}</span>
            </div>
            <div style="color:var(--text-lo); margin-top:2px">${t('diplomacy.archive_status', { status: a.status })}</div>
          </div>`).join('')}
          <button class="dash-link" style="margin-top:8px">${t('diplomacy.btn_load_older')}</button>
        </div>
      </div>
    </div>
  `;

  // Tab switching
  const tabs = [
    { btn: '#dtab-nap',   view: '#dview-nap'   },
    { btn: '#dtab-spy',   view: '#dview-spy'   },
    { btn: '#dtab-intel', view: '#dview-intel' },
    { btn: '#dtab-inbox', view: '#dview-inbox' },
  ];
  tabs.forEach(({ btn }, idx) => {
    wrapper.querySelector(btn)!.addEventListener('click', () => {
      tabs.forEach(({ btn: b, view: v }, i) => {
        (wrapper.querySelector(b) as HTMLElement).classList.toggle('active', i === idx);
        (wrapper.querySelector(v) as HTMLElement).style.display = i === idx ? '' : 'none';
      });
    });
  });

  root.appendChild(wrapper);

  root.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('.corp-link') as HTMLElement | null;
    if (link?.dataset.corp) openCorpDossier(link.dataset.corp);
  });

  return root;
}
